import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const errText = (e) => (e?.response ? `HTTP ${e.response.status}: ${typeof e.response.data?.detail === "string" ? e.response.data.detail : JSON.stringify(e.response.data?.detail ?? "")}` : "Network error — check the run list before retrying");

function Typed({ verb, label, placeholder, onGo, disabled }) {
  const [ref, setRef] = useState("");
  const [text, setText] = useState("");
  const v = verb.toLowerCase();
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <input value={ref} onChange={(e) => setRef(e.target.value.trim())} placeholder={placeholder} className="border border-stone-300 rounded px-2 py-1 text-xs font-mono w-80" data-testid={`fee-candidates-${v}-ref`} />
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`${verb} <${placeholder}>`} className="border border-stone-300 rounded px-2 py-1 text-xs font-mono w-96" data-testid={`fee-candidates-${v}-confirm`} />
      <Button size="sm" variant="outline" disabled={disabled || !ref || text !== `${verb} ${ref}`} onClick={() => onGo(ref, text)} data-testid={`fee-candidates-${v}-btn`}>{label}</Button>
    </div>
  );
}

export function FeeCandidatesCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const load = useCallback(() => api.get("/admin/fee-candidates").then((r) => setS(r.data)).catch((e) => setLast(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  const go = (path) => async (ref, confirm) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/fee-candidates/${path}`, { ref, confirm });
      setLast(`${new Date().toLocaleTimeString()} · ${path}: ${JSON.stringify({ ...r.data, conflicts: r.data.conflicts?.length ?? r.data.conflicts, kept_later_edits: r.data.kept_later_edits?.length })}`);
      toast.success(`${path} finished`);
    } catch (e) {
      setLast(`${new Date().toLocaleTimeString()} · ${errText(e)}`);
      toast.error(errText(e));
    } finally { setBusy(false); load(); }
  };
  if (!s) return <section className="mt-8 text-sm text-stone-500" data-testid="fee-candidates-card">Loading fee-candidate plan… {last}</section>;
  return (
    <section className="mt-8 rounded-lg border border-stone-200 bg-white p-4" data-testid="fee-candidates-card">
      <h2 className="flex items-center gap-2 font-medium text-stone-900"><Receipt className="h-4 w-4" /> Reviewed fee plan (MIT 2026-27 tuition + US application fees)</h2>
      <p className="text-xs text-stone-600 mt-1 break-all">DB {s.db_name} · digest <span className="font-mono" data-testid="fee-candidates-digest">{s.digest}</span></p>
      <p className="text-xs text-stone-600" data-testid="fee-candidates-summary">{Object.entries(s.summary).map(([k, v]) => `${k}: ${v}`).join(" · ")} · skipped {s.skipped.length}</p>
      <p className={`text-xs mt-1 ${s.apply_enabled ? "text-amber-700" : "text-stone-500"}`} data-testid="fee-candidates-enabled">{s.apply_enabled ? "Apply/rollback ENABLED on this server" : "Apply/rollback disabled on this server"}</p>
      <ul className="mt-2 divide-y divide-stone-100">
        {s.runs.map((r) => <li key={r.run_id} className="py-1 text-xs" data-testid={`fee-candidates-run-${r.run_id}`}><span className="font-mono">{r.run_id}</span> · {r.status} · modified {r.modified ?? "…"} · conflicts {r.conflicts ?? "…"}{r.restored != null ? ` · restored ${r.restored}` : ""}</li>)}
      </ul>
      {busy && <p className="text-xs text-amber-700 mt-2 flex items-center gap-1" data-testid="fee-candidates-busy"><Loader2 className="h-3 w-3 animate-spin" /> Working — don't click again</p>}
      {last && <p className="text-xs text-stone-700 mt-2 break-words" data-testid="fee-candidates-last-result">{last}</p>}
      <Typed verb="APPLY" label="Apply reviewed plan" placeholder="digest" onGo={go("apply")} disabled={!s.apply_enabled || busy} />
      <Typed verb="ROLLBACK" label="Roll back" placeholder="run id" onGo={go("rollback")} disabled={!s.apply_enabled || busy} />
    </section>
  );
}
